import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import Stack from '@mui/material/Stack';

import { AppButton, WhatsAppButton } from '@/components/atoms';
import { type PageIntroActionsPlan } from '@/hooks/usePageIntroActions';

export { type PageIntroAction } from '@/hooks/usePageIntroActions';

type Props = { plan: PageIntroActionsPlan };

/**
 * The call to action inside the opening band — the row, and nothing else.
 *
 * Every decision this used to make is in `usePageIntroActions`: which button
 * is filled, what WhatsApp says, which link shape an action takes, and which
 * widths see the row at all. What is left is the arrangement, and the one
 * thing that is genuinely a property of the drawing rather than the policy:
 * WhatsApp carries its own `display` rather than being left out of the tree,
 * because the tree is written once at build time for every width (the site
 * prerenders with `ssr: false`) and only CSS can still tell them apart
 * afterwards.
 *
 * Renders nothing when the page asked for neither, so a copy-only band does
 * not carry an empty row of margin.
 */
export function PageIntroActions({ plan }: Props) {
  if (plan.empty) return null;

  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} sx={{ alignItems: 'flex-start' }}>
      {plan.action && (
        <AppButton
          {...plan.action.link}
          size="large"
          endIcon={<ArrowForwardRounded />}
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          {plan.action.label}
        </AppButton>
      )}
      {plan.whatsappMessage && (
        <WhatsAppButton
          message={plan.whatsappMessage}
          size="large"
          {...(plan.stepDown ? { emphasis: 'outline' as const } : {})}
          // Out below `md`: the sticky bar at the foot of every phone viewport
          // already offers WhatsApp (§60 L), and two of the same affordance on
          // one screen is the duplication that bar exists to prevent.
          sx={{ display: { xs: 'none', md: 'inline-flex' }, width: { xs: '100%', sm: 'auto' } }}
        >
          Ask on WhatsApp
        </WhatsAppButton>
      )}
    </Stack>
  );
}
